import {
  IDENTITY_PLACEMENT,
  placementsDiffer,
  type GeoPlacement,
} from '@viewer/lib/geoTransform'

// ---------------------------------------------------------------------------
// Leitura de coordenadas para o utilizador.
//
// O mundo do three tem o Y para CIMA e o Z para SUL (x = Este, y = Cima,
// z = -Norte). O IFC, o Revit e qualquer CRS têm o Z para cima e o Y
// para Norte. Mostrar o vector do three com rótulos X/Y/Z punha a cota na
// coluna "Y" e o Norte com o sinal trocado na coluna "Z", e contradizia o
// plano de trabalho "XY (planta)", que já usa a convenção do IFC.
//
// Aqui o ponto do mundo passa para a convenção do IFC (x, -z, y). O que
// isso quer dizer depende do referencial:
//   - sem georreferenciação aplicada, o mundo coincide com as coordenadas
//     do FICHEIRO (o recentramento do conversor é desfeito no load):
//     rótulos X, Y, Z;
//   - com georreferenciação aplicada, o mundo está no CRS do mapa:
//     rótulos E, N, Cota.
//
// Puro: números a entrar, rótulos e números a sair.
// ---------------------------------------------------------------------------

export type CoordFrame = 'ficheiro' | 'georref'

export interface CoordAxis {
  label: string
  value: number
}

/** Rótulos dos três eixos, na ordem em que se mostram. */
export function coordAxisLabels(frame: CoordFrame): [string, string, string] {
  return frame === 'georref' ? ['E', 'N', 'Cota'] : ['X', 'Y', 'Z']
}

/** Nome do referencial, para a linha de cabeçalho. */
export function coordFrameLabel(frame: CoordFrame): string {
  return frame === 'georref'
    ? 'georreferenciadas (CRS do modelo)'
    : 'do ficheiro IFC'
}

/** Ponto (ou diferença) do mundo three na convenção do IFC: (x, -z, y). */
export function worldToIfcAxes(
  p: readonly [number, number, number],
): [number, number, number] {
  const n = -p[2]
  return [p[0], n === 0 ? 0 : n, p[1]]
}

export function coordReadout(
  p: readonly [number, number, number],
  frame: CoordFrame,
): [CoordAxis, CoordAxis, CoordAxis] {
  const v = worldToIfcAxes(p)
  const l = coordAxisLabels(frame)
  return [
    { label: l[0], value: v[0] },
    { label: l[1], value: v[1] },
    { label: l[2], value: v[2] },
  ]
}

/** O referencial em que o mundo está: georreferenciado quando algum
 *  modelo carregado tem um placement efectivo (override ou o lido do
 *  ficheiro) diferente da identidade. */
export function coordFrameFor(
  modelIds: readonly string[],
  baselines: Readonly<Record<string, GeoPlacement | undefined>>,
  overrides: Readonly<Record<string, GeoPlacement | undefined>>,
): CoordFrame {
  for (const id of modelIds) {
    const p = overrides[id] ?? baselines[id]
    if (p && placementsDiffer(p, IDENTITY_PLACEMENT)) return 'georref'
  }
  return 'ficheiro'
}
